import { axiosRequest, type AxiosResult } from "@/lib/axios";
import type { Review, ReviewRequest, ReviewResponse, ReviewUpdateRequest, ReviewPaginatedResponse, ReviewFilters, ReviewSort, ReviewStatusCounts } from "../types";

const API_BASE_URL = import.meta.env.VITE_SERVICE_CORE_URL;

function buildFilterParams(filters?: ReviewFilters, sort?: ReviewSort): URLSearchParams {
	const params = new URLSearchParams();

	if (!filters) return params;

	if (filters.status) {
		const value = Array.isArray(filters.status) ? filters.status.join(",") : filters.status;
		params.set("filter[status]", value);
	}
	if (filters.source) {
		const value = Array.isArray(filters.source) ? filters.source.join(",") : filters.source;
		params.set("filter[source]", value);
	}
	if (filters.type) {
		params.set("filter[type]", filters.type);
	}
	if (filters.content_type) {
		const value = Array.isArray(filters.content_type) ? filters.content_type.join(",") : filters.content_type;
		params.set("filter[content_type]", value);
	}
	if (filters.form) {
		params.set("filter[form]", filters.form);
	}
	if (filters.rating) {
		params.set("filter[rating]", filters.rating);
	}
	if (filters.tags && filters.tags.length > 0) {
		params.set("filter[tags]", filters.tags.join(","));
		if (filters.tags_mode) {
			params.set("filter[tags_mode]", filters.tags_mode);
		}
	}
	if (filters.date) {
		params.set("filter[date]", filters.date);
	}
	if (filters.customer_avatar_url) {
		params.set("filter[customer_avatar_url]", filters.customer_avatar_url);
	}
	if (filters.customer_company_logo_url) {
		params.set("filter[customer_company_logo_url]", filters.customer_company_logo_url);
	}

	if (sort) {
		// Format: -created_at (desc) or created_at (asc)
		const sortValue = sort.direction === "desc" ? `-${sort.order_by}` : sort.order_by;
		params.set("sort", sortValue);
	}

	return params;
}

export const reviewApi = {
	index: async (workspaceId: string, cursor?: string | null, filters?: ReviewFilters): Promise<AxiosResult<ReviewPaginatedResponse>> => {
		const params = buildFilterParams(filters);
		if (cursor) {
			params.set("cursor", cursor);
		}
		const queryString = params.toString();
		const url = `${API_BASE_URL}/${workspaceId}/reviews${queryString ? `?${queryString}` : ""}`;
		const result = await axiosRequest<ReviewPaginatedResponse | Review[]>({
			url,
			method: "GET",
			preserveFullResponse: true,
		});

		return result as AxiosResult<ReviewPaginatedResponse>;
	},

	indexForWallOfLove: async (
		workspaceId: string,
		cursor?: string | null,
		filters?: ReviewFilters,
		sort?: ReviewSort,
		locallyPinnedReviewIds?: string[],
		perPage?: number,
	): Promise<AxiosResult<ReviewPaginatedResponse>> => {
		const body: {
			filter: Record<string, string | string[]>;
			sort?: string;
			cursor?: string;
			per_page?: number;
		} = {
			filter: {
				status: "approved",
			},
		};

		// Add filters from ReviewFilters
		if (filters) {
			if (filters.source) {
				body.filter.source = Array.isArray(filters.source) ? filters.source.join(",") : filters.source;
			}
			if (filters.type) {
				body.filter.type = filters.type;
			}
			if (filters.content_type) {
				body.filter.content_type = Array.isArray(filters.content_type) ? filters.content_type.join(",") : filters.content_type;
			}
			if (filters.form) {
				body.filter.form = filters.form;
			}
			if (filters.rating) {
				body.filter.rating = filters.rating;
			}
			if (filters.tags && filters.tags.length > 0) {
				body.filter.tags = filters.tags;
				if (filters.tags_mode) {
					body.filter.tags_mode = filters.tags_mode;
				}
			}
			if (filters.date) {
				body.filter.date = filters.date;
			}
	if (filters.customer_avatar_url) {
		body.filter.customer_avatar_url = filters.customer_avatar_url;
	}
	if (filters.customer_company_logo_url) {
		body.filter.customer_company_logo_url = filters.customer_company_logo_url;
	}
		}

		// Add exclude logic
		if (locallyPinnedReviewIds && locallyPinnedReviewIds.length > 0) {
			body.filter.exclude_ids = locallyPinnedReviewIds;
		}

		// Add sort
		if (sort) {
			body.sort = sort.direction === "desc" ? `-${sort.order_by}` : sort.order_by;
		}

		// Add cursor for pagination
		if (cursor) {
			body.cursor = cursor;
		}

		// Add per_page
		if (perPage) {
			body.per_page = perPage;
		}

		const url = `${API_BASE_URL}/${workspaceId}/reviews/search`;
		const result = await axiosRequest<ReviewPaginatedResponse | Review[]>({
			url,
			method: "POST",
			data: body,
			preserveFullResponse: true,
		});

		return result as AxiosResult<ReviewPaginatedResponse>;
	},

	create: (workspaceId: string, payload: ReviewRequest): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews`, method: "POST", data: payload });
	},

	show: (workspaceId: string, reviewId: string): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}`, method: "GET" });
	},

	update: (workspaceId: string, id: string, request: ReviewUpdateRequest): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/${id}`, method: "PATCH", data: request });
	},

	delete: (workspaceId: string, id: string): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/${id}`, method: "DELETE" });
	},

	approve: (workspaceId: string, reviewId: string): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/approve`, method: "PATCH" });
	},

	unapprove: (workspaceId: string, reviewId: string): Promise<AxiosResult<ReviewResponse>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/${reviewId}/unapprove`, method: "PATCH" });
	},

	getByIds: (workspaceId: string, ids: string[]): Promise<AxiosResult<Review[]>> => {
		return axiosRequest({ url: `${API_BASE_URL}/${workspaceId}/reviews/by-ids`, method: "POST", data: { ids } });
	},

	statusCounts: (workspaceId: string, filters?: ReviewFilters): Promise<AxiosResult<ReviewStatusCounts>> => {
		const params = buildFilterParams(filters);
		const queryString = params.toString();
		const url = `${API_BASE_URL}/${workspaceId}/reviews/status/counts${queryString ? `?${queryString}` : ""}`;
		return axiosRequest({ url, method: "GET" });
	},

	indexDrafts: async (workspaceId: string, cursor?: string | null): Promise<AxiosResult<ReviewPaginatedResponse>> => {
		const params = new URLSearchParams();
		if (cursor) {
			params.set("cursor", cursor);
		}
		const queryString = params.toString();
		const url = `${API_BASE_URL}/${workspaceId}/reviews/drafts${queryString ? `?${queryString}` : ""}`;
		const result = await axiosRequest<ReviewPaginatedResponse | Review[]>({
			url,
			method: "GET",
			preserveFullResponse: true,
		});

		return result as AxiosResult<ReviewPaginatedResponse>;
	},
};
